import React, { useState, useEffect } from 'react';
import { Colors } from '../../../styles';
import { AvailabilityStore } from '../../../stores/availability/availabilityStore';
import { observer } from 'mobx-react-lite';

export const AvailabilityConfig = observer(() => {
  const store = AvailabilityStore();
  const [newBlockedDate, setNewBlockedDate] = useState('');
  const [newEnabledDate, setNewEnabledDate] = useState('');
  const [newHour, setNewHour] = useState('');

  useEffect(() => {
    store.get();
  }, []);

  const daysOfWeek = [
    { id: 0, name: 'Domingo' },
    { id: 1, name: 'Segunda' },
    { id: 2, name: 'Terça' },
    { id: 3, name: 'Quarta' },
    { id: 4, name: 'Quinta' },
    { id: 5, name: 'Sexta' },
    { id: 6, name: 'Sábado' },
  ];

  const handleDayToggle = (dayId: number) => {
    const currentDays = [...store.settings.allowed_days];
    const index = currentDays.indexOf(dayId);
    if (index > -1) {
      currentDays.splice(index, 1);
    } else {
      currentDays.push(dayId);
    }
    store.save({ ...store.settings, allowed_days: currentDays.sort() });
  };

  const handleAddHour = () => {
    if (!newHour) return;
    const currentHours = [...store.settings.allowed_hours];
    if (!currentHours.includes(newHour)) {
      currentHours.push(newHour);
      store.save({ ...store.settings, allowed_hours: currentHours.sort() });
    }
    setNewHour('');
  };

  const handleRemoveHour = (hour: string) => {
    const currentHours = store.settings.allowed_hours.filter((h: string) => h !== hour);
    store.save({ ...store.settings, allowed_hours: currentHours });
  };

  const handleAddBlockedDate = () => {
    if (!newBlockedDate) return;
    const currentBlocked = [...store.settings.blocked_dates];
    if (!currentBlocked.includes(newBlockedDate)) {
      currentBlocked.push(newBlockedDate);
      store.save({ ...store.settings, blocked_dates: currentBlocked.sort() });
    }
    setNewBlockedDate('');
  };

  const handleRemoveBlockedDate = (date: string) => {
    const currentBlocked = store.settings.blocked_dates.filter((d: string) => d !== date);
    store.save({ ...store.settings, blocked_dates: currentBlocked });
  };

  const handleAddEnabledDate = () => {
    if (!newEnabledDate) return;
    const currentEnabled = [...(store.settings.enabled_dates || [])];
    if (!currentEnabled.includes(newEnabledDate)) {
      currentEnabled.push(newEnabledDate);
      store.save({ ...store.settings, enabled_dates: currentEnabled.sort() });
    }
    setNewEnabledDate('');
  };

  const handleRemoveEnabledDate = (date: string) => {
    const currentEnabled = (store.settings.enabled_dates || []).filter((d: string) => d !== date);
    store.save({ ...store.settings, enabled_dates: currentEnabled });
  };

  return (
    <div className="bg-white dark:text-gray-200 dark:bg-secondary-dark-bg h-auto rounded-2xl w-full md:w-800 p-6 m-3 shadow-md border-color box-border">
      <div className="flex justify-between items-center mb-4">
        <p className="text-xl font-bold">Configuração de Disponibilidade</p>
      </div>

      <div className="mb-6">
        <p className="font-semibold mb-2">Dias da Semana Liberados</p>
        <div className="flex flex-wrap gap-2">
          {daysOfWeek.map((day) => (
            <label key={day.id} className="flex items-center space-x-2 bg-gray-100 dark:bg-gray-800 p-2 rounded cursor-pointer">
              <input
                type="checkbox"
                checked={store.settings.allowed_days.includes(day.id)}
                onChange={() => handleDayToggle(day.id)}
                className="w-4 h-4"
              />
              <span>{day.name}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="mb-6">
        <p className="font-semibold mb-2">Horários Disponíveis</p>
        <p className="text-xs text-gray-500 mb-3 bg-blue-50 dark:bg-gray-800 p-2 rounded border border-blue-100 dark:border-gray-700">
          ℹ️ O App exibe por padrão uma grade de: <strong>08:00am, 10:00am, 12:00pm, 14:00pm e 16:00pm</strong>. 
          Use este campo para habilitar esses horários ou adicionar novos horários personalizados.
        </p>
        <div className="flex gap-2 mb-2">
          <input
            type="text"
            value={newHour}
            onChange={(e) => setNewHour(e.target.value)}
            placeholder="Ex: 09:00am ou 15:00pm"
            className="border p-2 rounded flex-1 dark:bg-gray-800 dark:border-gray-600"
          />
          <button
            onClick={handleAddHour}
            className="p-2 rounded text-white"
            style={{ backgroundColor: Colors.primary }}
          >
            Adicionar Hora
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {store.settings.allowed_hours.map((hour: string) => (
            <div key={hour} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full flex items-center">
              <span>{hour}</span>
              <button onClick={() => handleRemoveHour(hour)} className="ml-2 text-red-500 font-bold">×</button>
            </div>
          ))}
        </div>
      </div>

      <div className="mb-6">
        <p className="font-semibold mb-2 text-green-600">Datas Liberadas Específicas (Exceções)</p>
        <div className="flex gap-2 mb-2">
          <input
            type="date"
            value={newEnabledDate}
            onChange={(e) => setNewEnabledDate(e.target.value)}
            className="border p-2 rounded flex-1 dark:bg-gray-800 dark:border-gray-600"
          />
          <button
            onClick={handleAddEnabledDate}
            className="p-2 rounded text-white"
            style={{ backgroundColor: Colors.green_darker }}
          >
            Habilitar Data
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {(store.settings.enabled_dates || []).map((date: string) => (
            <div key={date} className="bg-green-100 text-green-800 px-3 py-1 rounded-full flex items-center">
              <span>{date}</span>
              <button onClick={() => handleRemoveEnabledDate(date)} className="ml-2 text-red-500 font-bold">×</button>
            </div>
          ))}
        </div>
      </div>

      <div>
        <p className="font-semibold mb-2 text-red-600">Datas Bloqueadas (Blackout)</p>
        <div className="flex gap-2 mb-2">
          <input
            type="date"
            value={newBlockedDate}
            onChange={(e) => setNewBlockedDate(e.target.value)}
            className="border p-2 rounded flex-1 dark:bg-gray-800 dark:border-gray-600"
          />
          <button
            onClick={handleAddBlockedDate}
            className="p-2 rounded text-white"
            style={{ backgroundColor: Colors.primary }}
          >
            Bloquear Data
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {store.settings.blocked_dates.map((date: string) => (
            <div key={date} className="bg-red-100 text-red-800 px-3 py-1 rounded-full flex items-center">
              <span>{date}</span>
              <button onClick={() => handleRemoveBlockedDate(date)} className="ml-2 text-red-500 font-bold">×</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
});
